
import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthRedirect from './components/auth/AuthRedirect';
import './App.css';

// Importer le composant Index directement, sans lazy loading
import Index from './pages/Index';

// Page de chargement commune
const LoadingPage = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="flex flex-col items-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
      <p className="text-muted-foreground">Chargement...</p>
    </div>
  </div>
);

// Lazy load pages to improve initial load performance
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const SubscriptionChange = lazy(() => import('./pages/SubscriptionChange'));
const SubscriptionReports = lazy(() => import('./pages/SubscriptionReports'));
const Team = lazy(() => import('./pages/Team'));
const History = lazy(() => import('./pages/History'));
const Reports = lazy(() => import('./pages/Reports'));
const StripeImplementation = lazy(() => import('./pages/StripeImplementation'));
const StripeApiDocs = lazy(() => import('./pages/StripeApiDocs'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: true,
    }
  }
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Suspense fallback={<LoadingPage />}>
            <Routes>
              {/* Routes publiques */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              {/* Redirection sur la route principale */}
              <Route path="/" element={<AuthRedirect />} />
              
              {/* Routes protégées (nécessitent authentification) */}
              <Route path="/index" element={
                <ProtectedRoute>
                  <Index />
                </ProtectedRoute>
              } />
              <Route path="/onboarding" element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              } />
              <Route path="/subscriptions" element={
                <ProtectedRoute>
                  <Subscriptions />
                </ProtectedRoute>
              } />
              <Route path="/subscription-change/:planId" element={
                <ProtectedRoute>
                  <SubscriptionChange />
                </ProtectedRoute>
              } />
              <Route path="/subscription-reports" element={
                <ProtectedRoute>
                  <SubscriptionReports />
                </ProtectedRoute>
              } />
              <Route path="/team" element={
                <ProtectedRoute>
                  <Team />
                </ProtectedRoute>
              } />
              <Route path="/history" element={
                <ProtectedRoute>
                  <History />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute>
                  <Reports />
                </ProtectedRoute>
              } />
              <Route path="/stripe-implementation" element={
                <ProtectedRoute>
                  <StripeImplementation />
                </ProtectedRoute>
              } />
              <Route path="/stripe-api-docs" element={
                <ProtectedRoute>
                  <StripeApiDocs />
                </ProtectedRoute>
              } />
              
              {/* Redirection par défaut pour la page principale */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
